import React, { useState, useEffect } from "react";
import {
    Card,
    Breadcrumb,
    Divider,
    Button,
    Form,
    Input,
    Select,
    message,
    Popconfirm,
    Space,
    Tag,
    Row,
    Col,
    Spin,
    Typography,
    Descriptions,
} from "antd";
import {
    HomeOutlined,
    EditOutlined,
    DeleteOutlined,
    BankOutlined,
    QrcodeOutlined,
    ArrowLeftOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { paymentAccountAPIWithErrorHandling } from "../../../api/payment/paymentAccount";

const { Option } = Select;
const { Title, Text } = Typography;

export default function PaymentAccountEdit() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [accountData, setAccountData] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [bankList, setBankList] = useState([]);
    const [loadingBanks, setLoadingBanks] = useState(false);
    const [bankBin, setBankBin] = useState(null);
    const [form] = Form.useForm();
    const [selectedBankCode, setSelectedBankCode] = useState(null);

    // Template options for VietQR
    const templateOptions = [
        {
            value: "compact2",
            label: "Compact 2 (540x640)",
            description: "Mã QR, logo, thông tin chuyển khoản",
        },
        {
            value: "compact",
            label: "Compact (540x540)",
            description: "QR kèm logo VietQR, Napas, ngân hàng",
        },
        {
            value: "qr_only",
            label: "QR Only (480x480)",
            description: "Chỉ bao gồm mã QR đơn giản",
        },
        {
            value: "print",
            label: "Print (600x776)",
            description: "Đầy đủ thông tin chuyển khoản",
        },
    ];

    // Fetch banks from VietQR API
    const fetchBanks = async () => {
        try {
            setLoadingBanks(true);
            const response = await fetch("https://api.vietqr.io/v2/banks");
            const result = await response.json();

            if (result.code === "00" && result.data) {
                setBankList(result.data);
            } else {
                message.error("Không thể tải danh sách ngân hàng");
            }
        } catch (error) {
            message.error("Lỗi khi tải danh sách ngân hàng: " + error.message);
        } finally {
            setLoadingBanks(false);
        }
    };

    // Fetch account data
    const fetchAccount = async () => {
        try {
            setLoading(true);
            const response = await paymentAccountAPIWithErrorHandling.getAccount(id);
            setAccountData(response.data);
            form.setFieldsValue(response.data);
        } catch (error) {
            if (error.message.includes("404")) {
                message.error("Không tìm thấy tài khoản");
                navigate("/payment-account");
            } else {
                message.error(error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchAccount();
            fetchBanks();
        }
    }, [id]);

    // Handle bank selection
    const handleBankSelect = (value) => {
        setBankBin(value);
        const selectedBank = bankList.find((bank) => bank.bin === value);
        setSelectedBankCode(selectedBank?.code);
        if (selectedBank) {
            form.setFieldsValue({
                acqId: selectedBank.bin,
                bank_name: (selectedBank.name || selectedBank.shortName) + "-" + selectedBank.code,
            });
        }
    };

    // Handle inline edit save
    const handleInlineEdit = async (values) => {
        try {
            setLoading(true);
            const updatedData = { ...accountData, ...values, bankcode: selectedBankCode };
            await paymentAccountAPIWithErrorHandling.updateOrCreate(id, updatedData);
            message.success("Cập nhật thông tin thành công!");
            setEditMode(false);
            fetchAccount();
        } catch (error) {
            message.error(error.message);
            form.setFieldsValue(accountData);
        } finally {
            setLoading(false);
        }
    };

    // Cancel inline edit
    const handleCancelEdit = () => {
        setEditMode(false);
        form.setFieldsValue(accountData);
    };

    // Start inline edit
    const startInlineEdit = () => {
        setEditMode(true);
        setBankBin(accountData.acqId);
        form.setFieldsValue(accountData);
    };

    // Handle delete account
    const handleDelete = async () => {
        try {
            setLoading(true);
            await paymentAccountAPIWithErrorHandling.deleteAccount(id);
            message.success("Xóa tài khoản thành công!");
            navigate("/payment-account");
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Handle toggle status
    const handleToggleStatus = async () => {
        try {
            setLoading(true);
            await paymentAccountAPIWithErrorHandling.toggleStatus(id);
            message.success("Cập nhật trạng thái thành công!");
            fetchAccount();
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Get payment method icon
    const getPaymentIcon = (type) => {
        switch (type?.toUpperCase()) {
            case "BANK":
                return <BankOutlined className="text-blue-500" />;
            default:
                return <BankOutlined className="text-gray-500" />;
        }
    };

    // Get payment type display name
    const getPaymentTypeDisplay = (type) => {
        switch (type?.toUpperCase()) {
            case "BANK":
                return "Tài khoản ngân hàng";
            default:
                return "Không xác định";
        }
    };

    // Get template display name
    const getTemplateDisplay = (template) => {
        const temp = templateOptions.find((t) => t.value === template);
        return temp ? temp.label : template || "Chưa chọn";
    };

    if (!accountData && !loading) {
        return null;
    }

    return (
        <div className="bg-gray-50 min-h-screen p-6">
            <Card className="shadow-sm">
                {/* Breadcrumb */}
                <div className="mb-6">
                    <Breadcrumb
                        separator=">"
                        className="mb-4"
                        items={[
                            {
                                href: "/",
                                title: (
                                    <>
                                        <HomeOutlined /> Trang chủ
                                    </>
                                ),
                            },
                            {
                                href: "/payment-account",
                                title: "Tài khoản thanh toán",
                            },
                            {
                                title: "Chi tiết",
                            },
                        ]}
                    />
                </div>

                <Divider />

                {/* Header */}
                <div className="mb-6">
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={4} className="mb-0">
                                Chi tiết tài khoản thanh toán
                            </Title>
                        </Col>
                        <Col>
                            <Space>
                                <Button
                                    icon={<ArrowLeftOutlined />}
                                    onClick={() => navigate("/payment-account")}
                                >
                                    Quay lại
                                </Button>
                                {!editMode ? (
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={startInlineEdit}
                                    >
                                        Chỉnh sửa
                                    </Button>
                                ) : (
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined />}
                                            onClick={() => form.submit()}
                                            loading={loading}
                                        >
                                            Lưu
                                        </Button>
                                        <Button onClick={handleCancelEdit}>Hủy</Button>
                                    </Space>
                                )}
                            </Space>
                        </Col>
                    </Row>
                </div>

                {/* Content */}
                <Spin spinning={loading}>
                    <Form
                        form={form}
                        onFinish={handleInlineEdit}
                        initialValues={accountData}
                    >
                        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                            <Row gutter={[24, 24]}>
                                <Col xs={24} lg={16}>
                                    <Descriptions
                                        title={
                                            <div className="flex items-center gap-2">
                                                {getPaymentIcon(accountData?.type)}
                                                <span>Thông tin tài khoản</span>
                                                {accountData?.is_default && (
                                                    <Tag color="gold">Mặc định</Tag>
                                                )}
                                            </div>
                                        }
                                        bordered
                                        column={1}
                                        size="middle"
                                    >
                                        <Descriptions.Item label="Tên tài khoản">
                                            {editMode ? (
                                                <Form.Item
                                                    name="name"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Vui lòng nhập tên tài khoản",
                                                        },
                                                        {
                                                            min: 2,
                                                            message: "Tên phải có ít nhất 2 ký tự",
                                                        },
                                                    ]}
                                                    style={{ margin: 0 }}
                                                >
                                                    <Input placeholder="Nhập tên tài khoản" />
                                                </Form.Item>
                                            ) : (
                                                <Text strong>{accountData?.name}</Text>
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Chủ tài khoản">
                                            {editMode ? (
                                                <Form.Item
                                                    name="account_holder"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Vui lòng nhập tên chủ tài khoản",
                                                        },
                                                        {
                                                            min: 2,
                                                            message: "Tên phải có ít nhất 2 ký tự",
                                                        },
                                                    ]}
                                                    style={{ margin: 0 }}
                                                >
                                                    <Input placeholder="Nhập tên chủ tài khoản" />
                                                </Form.Item>
                                            ) : (
                                                <Text strong>{accountData?.account_holder}</Text>
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Số tài khoản">
                                            {editMode ? (
                                                <Form.Item
                                                    name="account_number"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Vui lòng nhập số tài khoản",
                                                        },
                                                    ]}
                                                    style={{ margin: 0 }}
                                                >
                                                    <Input placeholder="Nhập số tài khoản" />
                                                </Form.Item>
                                            ) : (
                                                <Text code>{accountData?.account_number}</Text>
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Loại thanh toán">
                                            {editMode ? (
                                                <Form.Item
                                                    name="type"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Vui lòng chọn loại thanh toán",
                                                        },
                                                    ]}
                                                    style={{ margin: 0 }}
                                                >
                                                    <Select placeholder="Chọn loại thanh toán">
                                                        <Option value="BANK">
                                                            <Space>
                                                                <BankOutlined />
                                                                Tài khoản ngân hàng
                                                            </Space>
                                                        </Option>
                                                    </Select>
                                                </Form.Item>
                                            ) : (
                                                <Tag color="blue">
                                                    {getPaymentTypeDisplay(accountData?.type)}
                                                </Tag>
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Ngân hàng">
                                            {editMode ? (
                                                <>
                                                    <Select
                                                        showSearch
                                                        placeholder="Tìm và chọn ngân hàng"
                                                        optionFilterProp="children"
                                                        onChange={handleBankSelect}
                                                        value={bankBin}
                                                        filterOption={(input, option) =>
                                                            (option?.label ?? "")
                                                                .toLowerCase()
                                                                .includes(input.toLowerCase())
                                                        }
                                                        loading={loadingBanks}
                                                        style={{ width: "100%" }}
                                                    >
                                                        {bankList.map((bank) => (
                                                            <Option
                                                                key={bank.bin}
                                                                value={bank.bin}
                                                                label={`${bank.shortName || bank.name} - ${bank.bin}`}
                                                            >
                                                                <Space>
                                                                    <img
                                                                        src={bank.logo}
                                                                        alt={bank.shortName}
                                                                        style={{ width: 62, height: 28 }}
                                                                    />
                                                                    <span>{bank.shortName || bank.name}</span>
                                                                    <Text type="secondary">({bank.bin})</Text>
                                                                </Space>
                                                            </Option>
                                                        ))}
                                                    </Select>

                                                    <Form.Item
                                                        name="bank_name"
                                                        rules={[
                                                            { required: true, message: "Vui lòng chọn ngân hàng" },
                                                        ]}
                                                        style={{ marginTop: "5px" }}
                                                    >
                                                        <Input
                                                            placeholder="Tự động điền khi chọn ngân hàng"
                                                            disabled
                                                        />
                                                    </Form.Item>
                                                </>
                                            ) : (
                                                <Space>
                                                    {bankList.find((b) => b.bin === accountData?.acqId)
                                                        ?.logo && (
                                                            <img
                                                                src={
                                                                    bankList.find(
                                                                        (b) => b.bin === accountData?.acqId
                                                                    )?.logo
                                                                }
                                                                alt={accountData?.bank_name}
                                                                style={{ width: 20, height: 20 }}
                                                            />
                                                        )}
                                                    {accountData?.bank_name}
                                                </Space>
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Mã định danh ngân hàng (BIN)">
                                            {editMode ? (
                                                <Form.Item
                                                    name="acqId"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Vui lòng chọn ngân hàng",
                                                        },
                                                    ]}
                                                    style={{ margin: 0 }}
                                                >
                                                    <Input
                                                        placeholder="Tự động điền khi chọn ngân hàng"
                                                        disabled
                                                    />
                                                </Form.Item>
                                            ) : (
                                                <Text code>{accountData?.acqId}</Text>
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Mẫu QR Code">
                                            {editMode ? (
                                                <Form.Item
                                                    name="template"
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: "Vui lòng chọn mẫu QR",
                                                        },
                                                    ]}
                                                    style={{ margin: 0 }}
                                                >
                                                    <Select placeholder="Chọn mẫu QR Code" disabled={selectedBankCode !== 'OCB'}>
                                                        {templateOptions.map((temp) => (
                                                            <Option key={temp.value} value={temp.value}>
                                                                <div style={{ fontSize: "15px" }}>
                                                                    <QrcodeOutlined /> {temp.label} - {temp.description}
                                                                </div>
                                                            </Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                            ) : (
                                                <Tag color="cyan">
                                                    <QrcodeOutlined />{" "}
                                                    {getTemplateDisplay(accountData?.template)}
                                                </Tag>
                                            )}
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Trạng thái">
                                            <Space>
                                                <Tag
                                                    color={
                                                        accountData?.is_active ? "success" : "default"
                                                    }
                                                >
                                                    {accountData?.is_active
                                                        ? "Đang hoạt động"
                                                        : "Tạm dừng"}
                                                </Tag>
                                                {accountData?.is_default && (
                                                    <Tag color="gold">Tài khoản mặc định</Tag>
                                                )}
                                            </Space>
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Ngày tạo">
                                            <Text type="secondary">
                                                {accountData?.created_at
                                                    ? new Date(accountData.created_at).toLocaleString("vi-VN")
                                                    : "N/A"}
                                            </Text>
                                        </Descriptions.Item>

                                        <Descriptions.Item label="Cập nhật lần cuối">
                                            <Text type="secondary">
                                                {accountData?.updated_at
                                                    ? new Date(accountData.updated_at).toLocaleString("vi-VN")
                                                    : "N/A"}
                                            </Text>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Col>

                                <Col xs={24} lg={8}>
                                    <Card size="small" title="Thao tác">
                                        <Space direction="vertical" style={{ width: "100%" }}>
                                            <Button
                                                type={accountData?.is_active ? "default" : "primary"}
                                                onClick={handleToggleStatus}
                                                block
                                                disabled={editMode}
                                            >
                                                {accountData?.is_active ? "Tạm dừng" : "Kích hoạt"}
                                            </Button>

                                            <Popconfirm
                                                title="Xóa tài khoản thanh toán"
                                                description="Bạn có chắc chắn muốn xóa tài khoản này không?"
                                                onConfirm={handleDelete}
                                                okText="Xóa"
                                                cancelText="Hủy"
                                                okType="danger"
                                                disabled={editMode}
                                            >
                                                <Button
                                                    type="primary"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    block
                                                    disabled={editMode}
                                                >
                                                    Xóa tài khoản
                                                </Button>
                                            </Popconfirm>
                                        </Space>
                                    </Card>
                                </Col>
                            </Row>
                        </Card>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
}